/**
 * SubTypeCheckbox.tsx — 8208-A
 *
 * Individual plugin checkbox row inside an expanded category.
 * Shows check-selected.svg / an open circle for enabled/disabled state.
 *
 * SW-9063: when `isLastEnabled` is true (i.e. this checkbox is the only
 * enabled sub-type in its category), clicking and keyboard activation are
 * blocked to prevent the user from creating an invalid "zero enabled
 * plugins" state that would surface a 400 from /api/edit. The row is
 * visually muted via CSS (`.subtype-row--locked`) and labeled with
 * aria-disabled + title for AT users.
 */
import React from "react";
import styled from "styled-components";
import SVG from "react-inlinesvg";
import classnames from "classnames";
import { FeedbackPlugin } from "../../../shared/feedbackCategories";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SubTypeCheckboxProps {
  plugin: FeedbackPlugin;
  onToggle: (pluginId: string, currentEnabled: boolean) => void;
  /**
   * When true, this row represents the last remaining enabled sub-type in
   * its category. The user cannot toggle it off — every category must have
   * at least one enabled sub-type so the /api/edit request stays valid.
   * Disabled (unchecked) rows should always pass `false` so they remain
   * freely enable-able.
   */
  isLastEnabled?: boolean;
}

const LOCK_TOOLTIP = "At least one sub-type must remain enabled";
const CHECKED_ICON_OUTER_RADIUS = 8.4905;
const UNCHECKED_ICON_STROKE_WIDTH = 1.5;
const UNCHECKED_ICON_RADIUS = CHECKED_ICON_OUTER_RADIUS - UNCHECKED_ICON_STROKE_WIDTH / 2;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const SubTypeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 0 16px;
  cursor: pointer;

  &:hover .subtype-name {
    opacity: 0.8;
  }

  &.subtype-row--locked {
    cursor: not-allowed;
  }

  &.subtype-row--locked:hover .subtype-name {
    opacity: 1;
  }

  &.subtype-row--locked .subtype-checkbox-icon {
    opacity: 0.55;
  }
`;

const CheckboxIcon = styled(SVG)<{ $checked: boolean }>`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 2px;
  cursor: inherit;

  path {
    fill: ${({ $checked }) => ($checked ? "var(--primary-cta)" : "var(--grey-3)")};
  }
`;

const UncheckedCircleIcon = styled.svg`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 2px;
  cursor: inherit;

  circle {
    fill: none;
    stroke: var(--primary-cta);
  }
`;

const SubTypeText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  padding-left: 8px;
`;

const SubTypeName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--grey-0);
  line-height: 1.3;
`;

const SubTypeDesc = styled.div`
  font-size: 14px;
  color: var(--grey-0);
  line-height: 1.3;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SubTypeCheckbox({
  plugin,
  onToggle,
  isLastEnabled = false,
}: SubTypeCheckboxProps): JSX.Element {
  const handleToggle = () => {
    if (isLastEnabled) return;
    onToggle(plugin.id, plugin.enabled);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    if (isLastEnabled) {
      // Block keyboard activation on the locked row too — don't bypass the
      // guard the click handler enforces.
      e.preventDefault();
      return;
    }
    e.preventDefault();
    onToggle(plugin.id, plugin.enabled);
  };

  return (
    <SubTypeRow
      className={classnames("subtype-row", {
        "subtype-row--locked": isLastEnabled,
      })}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      aria-disabled={isLastEnabled ? "true" : undefined}
      title={isLastEnabled ? LOCK_TOOLTIP : undefined}
    >
      {plugin.enabled ? (
        <CheckboxIcon
          className="subtype-checkbox-icon"
          src="/assets/check-selected.svg"
          $checked={plugin.enabled}
          role="checkbox"
          aria-checked={plugin.enabled}
          aria-label={`Disable ${plugin.name}`}
        />
      ) : (
        <UncheckedCircleIcon
          className="subtype-checkbox-icon"
          viewBox="0 0 24 24"
          role="checkbox"
          aria-checked={plugin.enabled}
          aria-label={`Enable ${plugin.name}`}
        >
          <circle
            cx="12"
            cy="12"
            r={UNCHECKED_ICON_RADIUS}
            strokeWidth={UNCHECKED_ICON_STROKE_WIDTH}
          />
        </UncheckedCircleIcon>
      )}
      <SubTypeText className="subtype-text">
        <SubTypeName className="subtype-name">{plugin.name}</SubTypeName>
        {plugin.description && (
          <SubTypeDesc className="subtype-desc">
            {plugin.description as string}
          </SubTypeDesc>
        )}
      </SubTypeText>
    </SubTypeRow>
  );
}

export default SubTypeCheckbox;
